import { Add, FileDownloadOutlined, InfoOutlined } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
    Box, Button, Chip, IconButton,
    Stack, TextField, Typography,
} from "@mui/material";
import AppTable from "components/Shared/AppTable";
import TableFilter from "components/Shared/AppTable/TableFilter";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import { useTablePagination } from "hooks";
import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { localizeDateFull } from "utils/dateFormats";
import { useCustomParams } from "hooks/useCustomParams";
import {
    useLazyGetVendorProfileQuotationListQuery,
    useLazyExportVendorProfileQuotationListQuery,
    useLazyListAllVendorQuotationQuery,
    useLazyExportAllVendorQuotationQuery,
} from "store/services/vendorQuotationApi";
import { exportExcelFile } from "utils/exportExcelFile";
import CustomTabs from "components/Shared/CustomTabs";
import {
    useLazyExportLeadVendorQuotationQuery,
    useLazyListLeadVendorQuotationQuery,
} from "store/services/vendorLeadApi";


const breadcrumbs = [{ title: "Vendor Profile Quotation List" }];

const TABS = [
    { label: "All", value: "All" },
    { label: "Leads", value: "Leads" },
    { label: "Vendors", value: "Vendors" },
];

// ─── Shared filter bar ────────────────────────────────────────────────────────

const FilterBar = ({ startDate, endDate, setStartDate, setEndDate, onExport, isExporting }) => {
    const hasFilters = !!(startDate || endDate);
    return (
        <Box
            sx={{
                px: 2, pt: 2, pb: 1,
                display: "flex", gap: 2,
                alignItems: "center", flexWrap: "wrap",
                justifyContent: "flex-end",
            }}
        >
            <TextField
                label="Start Date" type="date" value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }} size="small"
            />
            <TextField
                label="End Date" type="date" value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }} size="small"
            />
            <TableFilter searchLabel="Search..." />
            <Button
                variant="outlined" size="small"
                onClick={() => { setStartDate(""); setEndDate(""); }}
                disabled={!hasFilters}
            >
                Clear
            </Button>
            <LoadingButton
                variant="contained" size="small" color="success"
                loading={isExporting} loadingPosition="start"
                startIcon={<FileDownloadOutlined />}
                onClick={onExport}
            >
                Export Excel
            </LoadingButton>
        </Box>
    );
};

// ─── Shared cell renderers ────────────────────────────────────────────────────

const quotationPdfCell = (row) =>
    row?.vendorQuotationPdf ? (
        <Stack spacing={0.5}>
            <Chip
                label={row.vendorQuotationPdf.title ?? row.vendorQuotationPdf.vendorQuotationId}
                size="small" color="error" variant="outlined"
            />
            <Typography variant="caption" color="text.secondary">
                {row.vendorQuotationPdf.vendorQuotationId}
            </Typography>
        </Stack>
    ) : (
        <Typography variant="caption" color="text.disabled">—</Typography>
    );

const quotationExcelCell = (row) =>
    row?.vendorQuotationExcel ? (
        <Stack spacing={0.5}>
            <Chip
                label={row.vendorQuotationExcel.title ?? row.vendorQuotationExcel.VendorQuotationExcelId}
                size="small" color="success" variant="outlined"
            />
            <Typography variant="caption" color="text.secondary">
                {row.vendorQuotationExcel.VendorQuotationExcelId}
            </Typography>
        </Stack>
    ) : (
        <Typography variant="caption" color="text.disabled">—</Typography>
    );

// ─── Column definitions ───────────────────────────────────────────────────────

// All tab — single unified table, normalized id/name from backend
const ALL_COLUMNS = [
    {
        name: "ID",
        selector: (row) => row?.id,
        cell: (row) => <div>{row?.id ?? "N/A"}</div>,
        minWidth: "150px",
    },
    {
        name: "Name",
        selector: (row) => row?.name,
        cell: (row) => <div>{row?.name ?? "N/A"}</div>,
        minWidth: "150px",
    },
    {
        name: "Type",
        selector: (row) => row?.type,
        cell: (row) => (
            <Chip
                label={row?.type ?? "N/A"}
                size="small"
                color={row?.type === "Lead" ? "warning" : "info"}
                variant="outlined"
            />
        ),
        minWidth: "100px",
    },
    {
        name: "PDF Quotation",
        selector: (row) => row?.vendorQuotationPdf?.title,
        cell: quotationPdfCell,
        minWidth: "180px",
    },
    {
        name: "Excel Quotation",
        selector: (row) => row?.vendorQuotationExcel?.title,
        cell: quotationExcelCell,
        minWidth: "180px",
    },
    {
        name: "Added By",
        selector: (row) => row?.addedBy?.name,
        cell: (row) => <div>{row?.addedBy?.name ?? "N/A"}</div>,
        minWidth: "130px",
    },
    {
        name: "Created At",
        selector: (row) => row?.createdAt,
        cell: (row) => <div>{row?.createdAt ? localizeDateFull(row.createdAt) : "N/A"}</div>,
        minWidth: "160px",
    },
];

const LEADS_COLUMNS = [
    {
        name: "Lead ID",
        selector: (row) => row?.leadId,
        cell: (row) => <div>{row?.leadId ?? "N/A"}</div>,
        minWidth: "140px",
    },
    {
        name: "Name",
        selector: (row) => row?.name,
        cell: (row) => <div>{row?.name ?? "N/A"}</div>,
        minWidth: "150px",
    },
    {
        name: "PDF Quotation",
        selector: (row) => row?.vendorQuotationPdf?.title,
        cell: quotationPdfCell,
        minWidth: "180px",
    },
    {
        name: "Excel Quotation",
        selector: (row) => row?.vendorQuotationExcel?.title,
        cell: quotationExcelCell,
        minWidth: "180px",
    },
    {
        name: "Added By",
        selector: (row) => row?.addedBy?.name,
        cell: (row) => <div>{row?.addedBy?.name ?? "N/A"}</div>,
        minWidth: "130px",
    },
    {
        name: "Created At",
        selector: (row) => row?.createdAt,
        cell: (row) => <div>{row?.createdAt ? localizeDateFull(row.createdAt) : "N/A"}</div>,
        minWidth: "160px",
    },
];

const VENDORS_COLUMNS = [
    {
        name: "Vendor Profile ID",
        selector: (row) => row?.vendorProfile?.vendorProfileId,
        cell: (row) => <div>{row?.vendorProfile?.vendorProfileId ?? "N/A"}</div>,
        minWidth: "150px",
    },
    {
        name: "Shop Name",
        selector: (row) => row?.vendorProfile?.shopName,
        cell: (row) => <div>{row?.vendorProfile?.shopName ?? "N/A"}</div>,
        minWidth: "150px",
    },
    {
        name: "PDF Quotation",
        selector: (row) => row?.vendorQuotationPdf?.title,
        cell: quotationPdfCell,
        minWidth: "180px",
    },
    {
        name: "Excel Quotation",
        selector: (row) => row?.vendorQuotationExcel?.title,
        cell: quotationExcelCell,
        minWidth: "180px",
    },
    {
        name: "Added By",
        selector: (row) => row?.addedBy?.name,
        cell: (row) => <div>{row?.addedBy?.name ?? "N/A"}</div>,
        minWidth: "130px",
    },
    {
        name: "Created At",
        selector: (row) => row?.createdAt,
        cell: (row) => <div>{row?.createdAt ? localizeDateFull(row.createdAt) : "N/A"}</div>,
        minWidth: "160px",
    },
    {
        name: "Actions",
        cell: (row) => (
            <Link to={`/app/crm/vendor-profile/${row?.vendorProfile?._id}/detail`}>
                <IconButton size="small" aria-label="View Vendor Profile">
                    <InfoOutlined color="primary" />
                </IconButton>
            </Link>
        ),
        ignoreRowClick: true,
        right: true,
        minWidth: "80px",
    },
];

// ─── Main screen ──────────────────────────────────────────────────────────────

const VendorLeadQuotationPdfExcelListScreen = () => {
    const [searchParams] = useSearchParams();
    const activeTab = searchParams.get("tab") || "All";

    const { params } = useCustomParams();
    const keyword = params?.search || "";

    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");

    // Pagination
    const allPagination = useTablePagination();
    const leadsPagination = useTablePagination();
    const vendorsPagination = useTablePagination();

    // ── All tab — combined API
    const [fetchAllList, { data: allListRes, isLoading: isAllLoading, isFetching: isAllFetching }] =
        useLazyListAllVendorQuotationQuery();
    const [exportAllList, { isFetching: isAllExporting }] =
        useLazyExportAllVendorQuotationQuery();

    // ── Leads tab API
    const [fetchLeadList, { data: leadListRes, isLoading: isLeadLoading, isFetching: isLeadFetching }] =
        useLazyListLeadVendorQuotationQuery();
    const [exportLeadList, { isFetching: isLeadExporting }] =
        useLazyExportLeadVendorQuotationQuery();

    // ── Vendors tab API
    const [fetchVendorList, { data: vendorListRes, isLoading: isVendorLoading, isFetching: isVendorFetching }] =
        useLazyGetVendorProfileQuotationListQuery();
    const [exportVendorList, { isFetching: isVendorExporting }] =
        useLazyExportVendorProfileQuotationListQuery();

    const sharedParams = {
        keyword,
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
    };

    // ── Fetch effects
    React.useEffect(() => {
        if (activeTab === "All") {
            fetchAllList({ ...sharedParams, page: allPagination.currentPage, limit: allPagination.perPage });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, keyword, startDate, endDate, allPagination.currentPage, allPagination.perPage]);

    React.useEffect(() => {
        if (activeTab === "Leads") {
            fetchLeadList({ ...sharedParams, page: leadsPagination.currentPage, limit: leadsPagination.perPage });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, keyword, startDate, endDate, leadsPagination.currentPage, leadsPagination.perPage]);

    React.useEffect(() => {
        if (activeTab === "Vendors") {
            fetchVendorList({ ...sharedParams, page: vendorsPagination.currentPage, limit: vendorsPagination.perPage });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, keyword, startDate, endDate, vendorsPagination.currentPage, vendorsPagination.perPage]);

    // ── Export helpers
    const buildExportFileName = (prefix) => {
        const parts = [prefix];
        if (keyword) parts.push(keyword);
        if (startDate || endDate) parts.push(`${startDate || "N/A"} to ${endDate || "N/A"}`);
        if (!keyword && !startDate && !endDate) parts.push(new Date().toISOString().slice(0, 10));
        return parts.join(" - ");
    };

    const handleAllExport = async () => {
        const res = await exportAllList(sharedParams);
        const rows = res?.data?.data ?? [];
        if (!rows.length) return;
        exportExcelFile({
            fileName: buildExportFileName("All Vendor Quotation List"),
            sheetData: rows.map((row) => ({
                "Type": row?.type ?? "N/A",
                "ID": row?.id ?? "N/A",
                "Name": row?.name ?? "N/A",
                "PDF Quotation Title": row?.vendorQuotationPdf?.title ?? "—",
                "PDF Quotation ID": row?.vendorQuotationPdf?.vendorQuotationId ?? "—",
                "Excel Quotation Title": row?.vendorQuotationExcel?.title ?? "—",
                "Excel Quotation ID": row?.vendorQuotationExcel?.VendorQuotationExcelId ?? "—",
                "Added By": row?.addedBy?.name ?? "N/A",
                "Created At": row?.createdAt ? localizeDateFull(row.createdAt) : "N/A",
            })),
        });
    };

    const handleLeadExport = async () => {
        const res = await exportLeadList(sharedParams);
        const rows = res?.data?.data ?? [];
        if (!rows.length) return;
        exportExcelFile({
            fileName: buildExportFileName("Lead Vendor Quotation List"),
            sheetData: rows.map((row) => ({
                "Lead ID": row?.leadId ?? "N/A",
                "Name": row?.name ?? "N/A",
                "PDF Quotation Title": row?.vendorQuotationPdf?.title ?? "—",
                "PDF Quotation ID": row?.vendorQuotationPdf?.vendorQuotationId ?? "—",
                "Excel Quotation Title": row?.vendorQuotationExcel?.title ?? "—",
                "Excel Quotation ID": row?.vendorQuotationExcel?.VendorQuotationExcelId ?? "—",
                "Added By": row?.addedBy?.name ?? "N/A",
                "Created At": row?.createdAt ? localizeDateFull(row.createdAt) : "N/A",
            })),
        });
    };

    const handleVendorExport = async () => {
        const res = await exportVendorList(sharedParams);
        const rows = res?.data?.data ?? [];
        if (!rows.length) return;
        exportExcelFile({
            fileName: buildExportFileName("Vendor Profile Quotation List"),
            sheetData: rows.map((row) => ({
                "Vendor Profile ID": row?.vendorProfile?.vendorProfileId ?? "N/A",
                "Shop Name": row?.vendorProfile?.shopName ?? "N/A",
                "Owner Name": row?.vendorProfile?.ownerName ?? "N/A",
                "PDF Quotation Title": row?.vendorQuotationPdf?.title ?? "—",
                "PDF Quotation ID": row?.vendorQuotationPdf?.vendorQuotationId ?? "—",
                "Excel Quotation Title": row?.vendorQuotationExcel?.title ?? "—",
                "Excel Quotation ID": row?.vendorQuotationExcel?.VendorQuotationExcelId ?? "—",
                "Added By": row?.addedBy?.name ?? "N/A",
                "Created At": row?.createdAt ? localizeDateFull(row.createdAt) : "N/A",
            })),
        });
    };

    const isBusy =
        isAllLoading || isAllFetching ||
        isLeadLoading || isLeadFetching ||
        isVendorLoading || isVendorFetching;

    return (
        <BreadcrumbLayout
            breadcrumbs={breadcrumbs}
            isBusy={isBusy}
            headerActions={<PageActions />}
        >
            <BreadcrumbLayout.Paper>
                <CustomTabs tabs={TABS} activeTab={activeTab} />

                {/* ── All Tab ── */}
                {activeTab === "All" && (
                    <>
                        <FilterBar
                            startDate={startDate} endDate={endDate}
                            setStartDate={setStartDate} setEndDate={setEndDate}
                            onExport={handleAllExport}
                            isExporting={isAllExporting}
                        />
                        <AppTable
                            columns={ALL_COLUMNS}
                            data={allListRes?.data}
                            paginationServer
                            paginationPerPage={allPagination.perPage}
                            paginationDefaultPage={allPagination.currentPage}
                            onChangePage={allPagination.handlePageChange}
                            onChangeRowsPerPage={allPagination.handlePerRowsChange}
                            progressPending={isAllLoading || isAllFetching}
                            paginationTotalRows={allListRes?.totalSize ?? 0}
                        />
                    </>
                )}

                {/* ── Leads Tab ── */}
                {activeTab === "Leads" && (
                    <>
                        <FilterBar
                            startDate={startDate} endDate={endDate}
                            setStartDate={setStartDate} setEndDate={setEndDate}
                            onExport={handleLeadExport}
                            isExporting={isLeadExporting}
                        />
                        <AppTable
                            columns={LEADS_COLUMNS}
                            data={leadListRes?.data}
                            paginationServer
                            paginationPerPage={leadsPagination.perPage}
                            paginationDefaultPage={leadsPagination.currentPage}
                            onChangePage={leadsPagination.handlePageChange}
                            onChangeRowsPerPage={leadsPagination.handlePerRowsChange}
                            progressPending={isLeadLoading || isLeadFetching}
                            paginationTotalRows={leadListRes?.totalSize ?? 0}
                        />
                    </>
                )}

                {/* ── Vendors Tab ── */}
                {activeTab === "Vendors" && (
                    <>
                        <FilterBar
                            startDate={startDate} endDate={endDate}
                            setStartDate={setStartDate} setEndDate={setEndDate}
                            onExport={handleVendorExport}
                            isExporting={isVendorExporting}
                        />
                        <AppTable
                            columns={VENDORS_COLUMNS}
                            data={vendorListRes?.data}
                            paginationServer
                            paginationPerPage={vendorsPagination.perPage}
                            paginationDefaultPage={vendorsPagination.currentPage}
                            onChangePage={vendorsPagination.handlePageChange}
                            onChangeRowsPerPage={vendorsPagination.handlePerRowsChange}
                            progressPending={isVendorLoading || isVendorFetching}
                            paginationTotalRows={vendorListRes?.totalSize ?? 0}
                        />
                    </>
                )}
            </BreadcrumbLayout.Paper>
        </BreadcrumbLayout>
    );
};

export default VendorLeadQuotationPdfExcelListScreen;

const PageActions = () => (
    <Box display="flex" gap={1}>
        <Link to="/app/crm/vendor-lead/add-pdf">
            <Button startIcon={<Add />} variant="contained">
                Add PDF to Vendor Profile
            </Button>
        </Link>
        <Link to="/app/crm/vendor-lead/add-excel">
            <Button startIcon={<Add />} variant="contained">
                Add Excel to Vendor Profile
            </Button>
        </Link>
    </Box>
);